import type { AccountEvm } from '@pkg/database-portfolio'
import type {
  Chain,
  InternalTransactionWithIndex,
  LowerHex,
  TransactionIndex,
} from '@pkg/evm'
import { retryWith401Handler } from '../utils'

/**
 * Parameters for fetching address transaction data.
 */
interface FetchAddressDataParams {
  account: AccountEvm
  chain: Chain
  getToken?: () => Promise<string | null>
  toBlock: number
  transactionCount: number
}

/**
 * Result object containing all fetched address transaction data.
 */
interface FetchAddressDataResult {
  indexes: readonly TransactionIndex[]
  internalTransactions: readonly InternalTransactionWithIndex[]
}

/**
 * Function type for fetching address transaction data.
 */
type FetchAddressData = (
  params: FetchAddressDataParams,
) => Promise<FetchAddressDataResult>

/**
 * Adds transaction indexes to a map, avoiding duplicates.
 * @param indexMap - The map to add indexes to
 * @param indexes - The array of transaction indexes to add
 */
const addIndexesToMap = (
  indexMap: Map<LowerHex, TransactionIndex>,
  indexes: readonly TransactionIndex[],
): void => {
  for (const index of indexes) {
    if (!indexMap.has(index.hash)) {
      indexMap.set(index.hash, index)
    }
  }
}

/**
 * Adds internal transaction indexes to a map, creating TransactionIndex objects.
 * @param indexMap - The map to add indexes to
 * @param internalTransactions - The array of internal transactions to process
 */
const addInternalTransactionIndexes = (
  indexMap: Map<LowerHex, TransactionIndex>,
  internalTransactions: readonly InternalTransactionWithIndex[],
): void => {
  for (const internalTransaction of internalTransactions) {
    if (!indexMap.has(internalTransaction.txHash)) {
      indexMap.set(internalTransaction.txHash, {
        hash: internalTransaction.txHash,
        blockNumber: internalTransaction.blockNumber,
        timestamp: internalTransaction.timestamp,
      })
    }
  }
}

const noRetryChainIds = new Set<number>([
  // Ronin
  2020,
])

/**
 * Fetches all transaction-related data for an address sequentially for Etherscan API chains.
 * Retrieves normal transactions, token transfers, and internal transactions.
 * @param chain - The blockchain chain configuration
 * @param address - The address to fetch data for
 * @param fromBlock - The starting block number
 * @param toBlock - The ending block number
 * @param getToken - Optional function to get authentication token
 * @returns Object containing all fetched transaction data
 */
const fetchTransactionData = async (
  chain: Chain,
  address: LowerHex,
  fromBlock: number,
  toBlock: number,
  getToken?: () => Promise<string | null>,
) => {
  const createRequestParams = () => ({
    address,
    fromBlock,
    toBlock,
    headers: getToken
      ? async () => createExplorerRequestHeaders({ getToken })
      : undefined,
  })

  const shouldUseRetry = !noRetryChainIds.has(chain.id)

  const executeApiCall = async <T>(apiCall: () => Promise<T>): Promise<T> => {
    if (shouldUseRetry) {
      return retryWith401Handler({ fn: apiCall })
    }
    return apiCall()
  }

  const normalTxIndexes = await executeApiCall(async () =>
    chain.explorer.getAddressTransactionIndexes(createRequestParams()),
  )

  const tokenTransferData = await executeApiCall(async () =>
    chain.explorer.getAddressTokenTransferIndexes(createRequestParams()),
  )

  const internalTransactions = await executeApiCall(async () =>
    chain.explorer.getAddressInternalTransactions(createRequestParams()),
  )

  return {
    normalTxIndexes,
    transferTxIndexes: tokenTransferData.indexes,
    internalTransactions,
  }
}

/**
 * Fetches and processes all transaction data for an EVM address.
 * Implements caching logic to avoid unnecessary API calls and combines
 * normal transactions, token transfers, and internal transactions.
 * @param params - The parameters for fetching address data
 * @returns Promise containing transaction indexes, internal transactions
 */
export const fetchAddressData: FetchAddressData = async (params) => {
  const { account, chain, getToken, toBlock } = params

  const address = account.evmAddress
  const fromBlock = account.evmToBlock ?? 0

  const { normalTxIndexes, transferTxIndexes, internalTransactions } =
    await fetchTransactionData(chain, address, fromBlock, toBlock, getToken)

  const indexMap = new Map<LowerHex, TransactionIndex>()
  addIndexesToMap(indexMap, normalTxIndexes)
  addIndexesToMap(indexMap, transferTxIndexes)
  addInternalTransactionIndexes(indexMap, internalTransactions)

  return {
    indexes: [...indexMap.values()],
    internalTransactions,
  }
}

/**
 * Parameters for creating explorer request headers.
 */
interface ExplorerRequestHeadersParams {
  getToken?: () => Promise<string | null>
}

/**
 * Creates request headers for blockchain explorer API calls.
 * Includes authentication token if available.
 * @param params - Parameters containing optional token getter function
 * @returns Headers object with Authorization bearer token, or undefined if no token
 */
const createExplorerRequestHeaders = async (
  params: ExplorerRequestHeadersParams,
): Promise<Record<string, string> | undefined> => {
  const { getToken } = params

  if (!getToken) {
    return undefined
  }

  const token = await getToken()

  return token ? { Authorization: `Bearer ${token}` } : undefined
}
